/**
 * rendering.ts
 * Rendering constants for Starfall Courier.
 * Parallax speed ratios and draw-budget limits — will be used by
 * src/game/rendering/GameCanvas.tsx and parallax layer renderers (Phase 1).
 *
 * Tile size, viewport dimensions, and frame-rate constants live in gameConstants.ts.
 *
 * Phase 0 stub — consumed by src/game/rendering/ (Phase 1).
 * See ROADMAP.md Phase 1.
 *
 * @module rendering
 */

// ── Sprite sheet toggle ──────────────────────────────────────────────────────
// Lives in constants/ to avoid a SpriteRenderer ↔ drawOffsets circular import.
/** When true, use PixelLab-generated Nova sprites; when false, the v4 sheet. */
export const USE_PIXELLAB_SHEET = true;

// ─── Parallax Layer Speed Ratios ─────────────────────────────────────────────
// Each ratio is multiplied by the camera scroll offset to get the layer's draw offset.
// 0 = static (sky). 1 = moves 1:1 with the world (foreground props).

/** Far background layer — distant sky / starfield. */
export const PARALLAX_FAR = 0.1;

/** Mid background layer — clouds, distant mountains. */
export const PARALLAX_MID = 0.4;

/** Near background layer — close scenery, tall grass, cave walls. */
export const PARALLAX_NEAR = 0.8;

// ─── Draw Budget ─────────────────────────────────────────────────────────────

/** Max Skia draw calls per frame (hard cap). AGENTS.md targets < 60. */
export const MAX_DRAW_CALLS = 59;

// ─── Greybox Colour Palette ──────────────────────────────────────────────────
// Shared between the Skia (GameCanvas.tsx) and Canvas2D (`*.web.ts`) renderers
// for visual parity. Used as the pre-decode fallback fill and as the authored
// colour for entities without a sprite (mines, boss bolts, fog, telegraphs).

/** Dark background colour for the greybox prototype. */
export const BG_COLOR = '#0d0d1a';

/** Platform fill colour for the greybox prototype. */
export const PLATFORM_COLOR = '#3a3a5c';

/** Platform top-edge highlight colour for the greybox prototype. */
export const PLATFORM_EDGE_COLOR = '#5a5a8c';

/** Nova colour by animation state — greybox stand-in for sprite frames. */
export const NOVA_COLORS: Record<string, string> = {
  IDLE:       '#e8c46a',  // gold
  WALK:       '#a4e86a',  // yellow-green
  RUN_START:  '#5ade92',  // light green (transition)
  RUN:        '#6ae8a4',  // green
  RUN_STOP:   '#5ade92',  // light green (transition)
  JUMP_CROUCH:'#6ab4e8',  // sky blue
  JUMP:       '#6ab4e8',  // sky blue
  FALL:       '#a86ae8',  // purple
  LAND:       '#8a6ae8',  // violet (transition)
  STOMP:      '#e84444',  // bright red dive
  HURT:       '#e86a6a',  // red
  DEAD:       '#555555',  // grey
  AIM_RIGHT:      '#e8c46a',
  AIM_UP_RIGHT:   '#e8c46a',
  AIM_UP:         '#e8c46a',
  AIM_UP_LEFT:    '#e8c46a',
  AIM_LEFT:       '#e8c46a',
  AIM_DOWN_LEFT:  '#e8c46a',
  AIM_DOWN:       '#e8c46a',
  AIM_DOWN_RIGHT: '#e8c46a',
  JUMP_FIRE_RIGHT:    '#6ab4e8',
  JUMP_FIRE_UP_RIGHT: '#6ab4e8',
} as const;

/** Exit portal glow fill colour (includes alpha). */
export const EXIT_GLOW_COLOR = '#f5c84233';

/** Exit portal hard-edge line colour. */
export const EXIT_LINE_COLOR = '#f5c842';

// ─── Space (World 2) Greybox Palette ────────────────────────────────────────
// Shared between the Skia and Canvas2D renderers until pixel-art sprites land.

/** Deep-space backdrop. Cooler than `BG_COLOR` so the two modes are
 *  distinguishable even on an empty screen. Also the fallback behind the
 *  painted starfield. */
export const SPACE_BG_COLOR = '#04060f';

/**
 * Parallax factor for the painted starfield (0 = locked, 1 = matches camera).
 * Distinct from {@link PARALLAX_FAR} (0.1): that's the W1 far layer behind two
 * faster layers; W2's single auto-scrolling backdrop reads as static at 0.1.
 */
export const SPACE_BG_PARALLAX_FACTOR = 0.25;

/**
 * Parallax factor for the mid-distance decoration layer (planets, moons, star
 * twinkles). Sits midway between the starfield (0.25) and the locked entity
 * layer (1.0) to read as a separate plane rather than a slow shimmer.
 */
export const SPACE_BG_DECORATION_PARALLAX_FACTOR = 0.5;

/** Courier ship hull colour (greybox stand-in for the ship sprite). */
export const SPACE_SHIP_COLOR = '#4fc3f7';

/**
 * Hull fill on alternate iframe blink half-cycles after a hit. Near-white so
 * the strobe never reads as the default `SPACE_SHIP_COLOR`. Pairs with
 * `SHIP_IFRAME_BLINK_RATE_HZ` in `spaceConstants.ts`.
 */
export const SPACE_SHIP_HIT_FLASH_COLOR = '#e8faff';

/** Ship-fired projectile colour. Pale laser-yellow — kept distinct from
 *  `COLORS.GOLD` (#f5c842, star seeds) so bolts don't read as collectibles. */
export const SPACE_PROJECTILE_COLOR = '#fff176';

/** Ship-fired missile colour (greybox). Warm orange — reads as a heavier
 *  munition than the laser bolt, distinct from the gold star-seed accent. */
export const SPACE_MISSILE_COLOR = '#ff8a3d';

/** Asteroid greybox fill keyed by size tier. Darker = denser/larger. */
export const SPACE_ASTEROID_COLORS = {
  sm: '#8a8aa0',
  md: '#6f6f86',
  lg: '#56566b',
  xl: '#3e3e54',
} as const;

/** Fighter drone greybox fill. Warm crimson — "hostile" against the cool ship
 *  blue and pale asteroid greys. Base of the hostile-red ramp. */
export const SPACE_ENEMY_FIGHTER_COLOR = '#d04848';

/** Bomber drone greybox fill. Deeper red than the fighter — reads as tougher. */
export const SPACE_ENEMY_BOMBER_COLOR = '#8a2a2a';

/** Shield satellite greybox fill. Cool violet, off the red ramp so it reads as
 *  a different threat category (orbital / armoured). */
export const SPACE_ENEMY_SATELLITE_COLOR = '#7a5cd4';

/** Star-Eater scout greybox fill. Near-void black — foreshadows the World 5
 *  boss; off both the red ramp and the satellite violet. */
export const SPACE_ENEMY_STAR_EATER_COLOR = '#1a0a24';

/** Void Mothership (World 2 boss) greybox fill. Deep oxblood — apex of the
 *  hostile-red ramp (fighter → bomber → mothership). */
export const SPACE_ENEMY_BOSS_COLOR = '#5a0f1a';

/** Boss turret projectile colour. Hot magenta — off both the red ramp and the
 *  ship-projectile yellow so players can tell their bolts from the boss's. */
export const SPACE_BOSS_PROJECTILE_COLOR = '#ff3aa3';

/** Boss homing-missile colour. Orange — keeps the three boss threat streams
 *  (red drones / magenta bolts / orange missiles) individually readable. */
export const SPACE_BOSS_MISSILE_COLOR = '#ff8a1f';

/** Fighter-drone laser colour. Bright lime — distinct from the magenta boss
 *  bolt and orange boss missile so the player can read which enemy fired. */
export const SPACE_FIGHTER_LASER_COLOR = '#aaff3a';

/** Bomber cluster-bomb colour. Bold orange — distinct from the boss
 *  missile's warmer orange and the fighter-laser lime so a dropped
 *  payload reads as its own threat stream during the fuse. */
export const SPACE_BOMBER_BOMB_COLOR = '#ff7a1f';

/** Bomb-shrapnel colour. Pale gold — visually downstream of the bomb's
 *  orange so the player parses each piece as a fragment of the parent. */
export const SPACE_BOMBER_SHRAPNEL_COLOR = '#ffcf66';

/** Nebula fog overlay for Level 2-3 (`#rrggbbaa`). Alpha tuned so enemies stay
 *  readable through it. Painted between projectiles and the player ship. */
export const SPACE_NEBULA_FOG_COLOR = '#5a2a8a40';

/** Homing-mine greybox fill by lifecycle state. IDLE = cool slate (inert
 *  hazard); ACTIVE = alarm red, distinct from the fighter red and boss orange
 *  so the trigger flip is unmistakable. */
export const SPACE_MINE_COLORS: Record<'IDLE' | 'ACTIVE', string> = {
  IDLE: '#5a6b7d',
  ACTIVE: '#ff4444',
} as const;



// --- Weapon Pickup Greybox Palette ------------------------------------------
// One colour per non-default `ProjectileKind`, evoking the payload (gem = cyan,
// boomerang = amber, fire = orange, ice = pale blue) and readable against both
// the W1 platform palette and the dark space backdrop.

/** Pickup fill keyed by weapon kind (matches `WeaponPickupKind`). */
export const WEAPON_PICKUP_COLORS: Record<'gem_shard' | 'boomerang' | 'fire_orb' | 'ice_crystal', string> = {
  gem_shard: '#42d4f5',
  boomerang: '#d49a3a',
  fire_orb: '#ff7a1f',
  ice_crystal: '#9ad6ff',
} as const;

/** Outline (top-edge highlight) colour applied above each pickup fill. */
export const WEAPON_PICKUP_OUTLINE_COLOR = '#ffffff';
